import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Select, Spin } from 'antd';
import ModalWrapper from '../ModalWrapper';
import { captureAnalytics } from '../../../actions/preload';
import '../MoveToReferralManagement/moveToReferralManagement.scss';

const MoveToInbox = ({
  hideModal, getInboxIngestionTargets, moveRecordToInbox, modalType, history,
}) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetId, setTargetId] = useState(null);
  const [runAi, setRunAi] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const target = targets.find((item) => item.id === targetId);

  useEffect(() => {
    let mounted = true;
    captureAnalytics('upload_move_to_inbox_opened');
    getInboxIngestionTargets().then((response) => {
      if (!mounted) return;
      if (!response || response.error) {
        setError('We could not load your Inbox queues. Close this window and try again.');
      } else {
        const queues = response.data?.fax_numbers || [];
        setTargets(queues);
        if (queues.length === 1) setTargetId(queues[0].id);
      }
      setLoading(false);
    }).catch(() => {
      if (!mounted) return;
      setError('We could not load your Inbox queues. Close this window and try again.');
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [getInboxIngestionTargets]);

  const move = async () => {
    if (!target || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    captureAnalytics('upload_move_to_inbox_submitted');
    try {
      const body = { fax_number_id: target.id };
      if (target.ai_opt_out_available) body.run_ai = runAi;
      const response = await moveRecordToInbox(modalType.data.record.file_token, body);
      if (!response || response.error) {
        setError(response?.response?.data?.error || 'We could not move this document. Please try again.');
        return;
      }
      captureAnalytics('upload_move_to_inbox_succeeded', {
        run_ai: target.ai_opt_out_available ? runAi : null,
      });
      hideModal();
      history.push('/app/received');
    } catch {
      setError('We could not move this document. Please try again.');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      modalTitle="Move to Inbox"
      actionName="Move to Inbox"
      dismiss={submitting ? null : 'Cancel'}
      closable={!submitting}
      hideModal={hideModal}
      action={move}
      disabled={loading || !target || submitting}
      isLoading={submitting}
      wrapClassName="mrm-modal-wrap"
    >
      <div className="mrm-modal">
        <p>
          Create a new Inbox document for your team to work on. The original will
          be archived in Uploads with its existing history. Previous workflow
          state and chart actions will stay with the original.
        </p>
        <section className="mrm-section">
          <div className="mrm-section__title">Inbox queue / department</div>
          {loading && <Spin size="small" />}
          {!loading && targets.length === 0 && !error && (
            <p>No Inbox queues are available. Contact your administrator to set one up.</p>
          )}
          {targets.length > 0 && (
            <Select
              aria-label="Inbox queue / department"
              placeholder="Select a queue or department"
              style={{ width: '100%' }}
              value={targetId}
              disabled={submitting}
              onChange={(id) => {
                setTargetId(id);
                setRunAi(true);
                captureAnalytics('upload_move_to_inbox_queue_selected');
              }}
            >
              {targets.map((queue) => (
                <Select.Option key={queue.id} value={queue.id}>
                  {queue.owner_name || queue.owner_type}
                  {' — '}
                  {queue.number}
                </Select.Option>
              ))}
            </Select>
          )}
        </section>
        {target?.ai_opt_out_available && (
          <Checkbox
            checked={runAi}
            disabled={submitting}
            onChange={(event) => {
              setRunAi(event.target.checked);
              captureAnalytics('upload_move_to_inbox_ai_toggled', { run_ai: event.target.checked });
            }}
          >
            Run AI to extract patient information
          </Checkbox>
        )}
        {error && <p role="alert" className="mrm-error">{error}</p>}
      </div>
    </ModalWrapper>
  );
};

MoveToInbox.propTypes = {
  hideModal: PropTypes.func.isRequired,
  getInboxIngestionTargets: PropTypes.func.isRequired,
  moveRecordToInbox: PropTypes.func.isRequired,
  modalType: PropTypes.shape({
    data: PropTypes.shape({ record: PropTypes.shape({ file_token: PropTypes.string.isRequired }).isRequired }).isRequired,
  }).isRequired,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
};

export default MoveToInbox;
